import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Save, RotateCcw, Eye, Pencil, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";

export default function LegalPagesManager() {
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState("");
  const [mode, setMode] = useState("edit"); // edit | preview
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/legal");
      setPages(data);
      if (!active && data.length) {
        setActive(data[0].slug);
        setDraft(data[0].content);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line

  const selectPage = (slug) => {
    const p = pages.find((x) => x.slug === slug);
    if (!p) return;
    setActive(slug);
    setDraft(p.content);
    setMode("edit");
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/legal/${active}`, { content: draft });
      toast.success(`${data.title} enregistré`);
      setPages((arr) => arr.map((p) => (p.slug === active ? { ...p, content: data.content, is_default: false } : p)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const restoreDefault = async () => {
    if (!active) return;
    if (!confirm("Restaurer le modèle par défaut ? Vos modifications actuelles seront perdues.")) return;
    try {
      const { data } = await api.post(`/admin/legal/${active}/reset`);
      setDraft(data.content);
      setPages((arr) => arr.map((p) => (p.slug === active ? { ...p, content: data.content, is_default: true } : p)));
      toast.success("Modèle par défaut restauré");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const activePage = pages.find((p) => p.slug === active);
  const dirty = activePage && draft !== activePage.content;

  return (
    <div className="space-y-6" data-testid="legal-pages-manager">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">// WiLink commercial</p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Pages légales</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Mentions légales, CGV et Politique de confidentialité — éditables en Markdown.
          Modèles RGPD pré-remplis, complétez les zones <code className="text-xs bg-muted px-1 rounded">[À COMPLÉTER]</code>.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : (
        <div className="grid md:grid-cols-[260px_1fr] gap-4">
          {/* Sidebar pages */}
          <aside className="space-y-2" data-testid="legal-pages-list">
            {pages.map((p) => (
              <button
                key={p.slug}
                onClick={() => selectPage(p.slug)}
                className={`w-full text-left p-3 rounded-md border transition-colors flex items-center gap-3 ${
                  active === p.slug ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-accent"
                }`}
                data-testid={`legal-tab-${p.slug}`}
              >
                <FileText className="h-4 w-4 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{p.title}</p>
                  <p className={`text-[10px] uppercase tracking-wider ${active === p.slug ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    {p.is_default ? "Modèle par défaut" : "Personnalisé"}
                  </p>
                </div>
              </button>
            ))}
          </aside>

          {/* Editor */}
          {activePage && (
            <section className="border border-border bg-card rounded-md overflow-hidden flex flex-col">
              <header className="flex items-center justify-between gap-2 p-3 border-b border-border bg-muted/30">
                <div className="flex gap-1">
                  <button
                    onClick={() => setMode("edit")}
                    className={`px-3 py-1.5 rounded text-xs font-medium inline-flex items-center gap-1.5 ${mode === "edit" ? "bg-foreground text-background" : "hover:bg-accent"}`}
                    data-testid="mode-edit"
                  >
                    <Pencil className="h-3 w-3" /> Édition
                  </button>
                  <button
                    onClick={() => setMode("preview")}
                    className={`px-3 py-1.5 rounded text-xs font-medium inline-flex items-center gap-1.5 ${mode === "preview" ? "bg-foreground text-background" : "hover:bg-accent"}`}
                    data-testid="mode-preview"
                  >
                    <Eye className="h-3 w-3" /> Aperçu
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/${active}`}
                    target="_blank"
                    className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                  >
                    Voir page publique <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
              </header>

              {mode === "edit" ? (
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  className="font-mono text-xs min-h-[60vh] border-0 rounded-none resize-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="legal-editor"
                />
              ) : (
                <div className="prose prose-neutral dark:prose-invert max-w-none p-6 min-h-[60vh] text-sm" data-testid="legal-preview">
                  <ReactMarkdown>{draft}</ReactMarkdown>
                </div>
              )}

              <footer className="flex items-center justify-between gap-2 p-3 border-t border-border bg-muted/30">
                <Button variant="outline" size="sm" onClick={restoreDefault} data-testid="legal-restore-default">
                  <RotateCcw className="h-3 w-3 mr-1.5" /> Modèle par défaut
                </Button>
                <Button size="sm" onClick={save} disabled={!dirty || saving} data-testid="legal-save">
                  <Save className="h-3 w-3 mr-1.5" /> {saving ? "Enregistrement…" : "Enregistrer"}
                </Button>
              </footer>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
